import { useEffect, useState } from "react";
import { X, Loader2, Search } from "lucide-react";
import api from "../../lib/api";

const KINDS = [
  { key: "customer", label: "Customer", endpoint: "/customers", labelKey: "name" },
  { key: "lead", label: "Lead", endpoint: "/leads", labelKey: "name" },
  { key: "ticket", label: "Ticket", endpoint: "/tickets", labelKey: "subject", codeKey: "ticket_no" },
  { key: "jobcard", label: "Job Card", endpoint: "/jobcards", labelKey: "description", codeKey: "job_no" },
  { key: "trip", label: "Trip", endpoint: "/trips", labelKey: "title" },
];

export default function LinkToPicker({ current, onClose, onSave }) {
  const [kind, setKind] = useState(current?.kind || "customer");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const spec = KINDS.find((k) => k.key === kind) || KINDS[0];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: "50" });
        if (q) params.set("q", q);
        const { data } = await api.get(`${spec.endpoint}?${params}`);
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data.items || []);
        setRows(items.slice(0, 50));
      } catch { /* silent */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, q, spec.endpoint]);

  const labelFor = (r) => {
    const code = spec.codeKey ? r[spec.codeKey] : null;
    const body = r[spec.labelKey] || "—";
    return code ? `${code} · ${body}` : body;
  };

  const save = (r) => {
    onSave?.({
      link_kind: kind,
      link_id: r.id,
      link_label: `${spec.label}: ${labelFor(r).slice(0, 60)}`,
    });
  };

  const clear = () => onSave?.({ link_kind: null, link_id: null, link_label: null });

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-start sm:items-center justify-center p-4 pt-16 sm:pt-4" onClick={onClose} data-testid="eng-link-picker">
      <div className="hv-card w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="font-display font-bold text-sm">Link this session</div>
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 space-y-2 shrink-0">
          <div className="flex gap-1 flex-wrap">
            {KINDS.map((k) => (
              <button
                key={k.key}
                onClick={() => setKind(k.key)}
                className={`text-[11px] px-2 py-1 rounded border transition ${kind === k.key ? "border-[#1FB6C1] bg-[#1FB6C1]/10 text-[#1FB6C1]" : "border-white/10 text-[#cbd2df] hover:border-[#1FB6C1]/40"}`}
                data-testid={`eng-link-kind-${k.key}`}
              >
                {k.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${spec.label}…`} className="hv-input text-sm pl-7 w-full" data-testid="eng-link-search" />
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6 text-[#647087]"><Loader2 className="w-4 h-4 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center text-xs text-[#a0aabf] py-6">No matches.</div>
          ) : (
            <ul>
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => save(r)}
                    className="w-full text-left px-3 py-2 hover:bg-white/[0.03] text-sm border-b border-white/5 transition"
                    data-testid={`eng-link-row-${r.id}`}
                  >
                    {labelFor(r)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {current?.id && (
          <div className="p-2 border-t border-white/10 shrink-0">
            <button onClick={clear} className="hv-btn hv-btn-ghost text-xs w-full" data-testid="eng-link-clear">
              Clear link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
